import { useState } from 'react';

import { createFileRoute, redirect } from '@tanstack/react-router';

import { jobsConfigApi } from '@/api/queries/jobsConfig';
import { isTenantAdmin, useTenantStore } from '@/auth/tenantStore';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';

import { TenantJobRuns } from '../-components/TenantJobRuns';
import { TenantJobSettings } from '../-components/TenantJobSettings';

// Tenant-side mirror of /admin/jobs/$key: tabbed Settings | Run history, where
// the run history shows only this tenant's slice of each global job run.
function TenantJobDetailRoute() {
  const { key } = Route.useParams();
  const [tab, setTab] = useState<'settings' | 'runs'>('settings');
  const { data: job, isPending } = jobsConfigApi.useTenantJob(key);

  useSetBreadcrumbs([
    { label: 'Settings' },
    { label: 'Jobs', to: '/settings/jobs' },
    { label: job?.displayName ?? key },
  ]);

  if (isPending || !job) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{job.displayName}</h1>
        <span style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 640 }}>{job.description}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {(['settings', 'runs'] as const).map((t) => {
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                color: active ? 'var(--text-1)' : 'var(--text-3)',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {t === 'settings' ? 'Settings' : 'Run history'}
            </button>
          );
        })}
      </div>

      {tab === 'settings' ? <TenantJobSettings job={job} /> : <TenantJobRuns jobKey={key} />}
    </div>
  );
}

export const Route = createFileRoute('/settings/jobs/$key')({
  beforeLoad: () => {
    if (!isTenantAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: TenantJobDetailRoute,
  errorComponent: ErrorComponent,
});
